import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { formatDate, formatDateTime } from '@utils/dateUtils'
import {
  Grid,
  Box,
  Typography,
  Divider,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Tooltip,
} from '@mui/material'
import {
  Edit,
  Print,
  PlayArrow,
  Check,
  Close,
  AttachFile,
  CloudUpload,
  Delete,
  Image,
  Description,
  InsertDriveFile,
  Pause,
  Refresh,
  Assignment,
} from '@mui/icons-material'
import { PageHeader, Button, Card, StatusChip, LoadingSpinner, Modal, PermissionGate } from '@components/common'
import { WorkOrderForm } from '@components/maintenance'
import { toast } from 'react-toastify'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'

const WorkOrderDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const { hasPermission, user } = useAuth()

  const [workOrder, setWorkOrder] = useState(null)
  const [jobCard, setJobCard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [attachments, setAttachments] = useState([])
  const [deleteAttachmentId, setDeleteAttachmentId] = useState(null)
  const [deleting, setDeleting] = useState(false)

  // Status action states
  const [statusLoading, setStatusLoading] = useState(false)
  const [editFormOpen, setEditFormOpen] = useState(false)
  const [completeModalOpen, setCompleteModalOpen] = useState(false)
  const [resolutionDescription, setResolutionDescription] = useState('')
  const [actualHours, setActualHours] = useState('')
  const [laborNotes, setLaborNotes] = useState('')
  const [jobCardLoading, setJobCardLoading] = useState(false)

  const fetchWorkOrder = async () => {
    try {
      const response = await api.get(`/maintenance/work-orders/${id}/`)
      setWorkOrder(response.data)
      setAttachments(response.data.attachment_files || [])
    } catch (error) {
      console.error('Error fetching work order:', error)
      toast.error('Failed to load work order')
    } finally {
      setLoading(false)
    }
  }

  const fetchJobCard = async () => {
    try {
      const response = await api.get('/maintenance/job-cards/', { params: { work_order: id } })
      const results = response.data.results || response.data
      setJobCard(results && results.length > 0 ? results[0] : null)
    } catch (error) {
      // User may lack job card view permission, or no job card exists yet; fail silently
      setJobCard(null)
    }
  }

  useEffect(() => {
    fetchWorkOrder()
    fetchJobCard()
  }, [id])

  // Status action handlers
  const handleStartWork = async () => {
    setStatusLoading(true)
    try {
      await api.post(`/maintenance/work-orders/${id}/start/`)
      toast.success('Work started successfully')
      fetchWorkOrder()
    } catch (error) {
      console.error('Error starting work:', error)
      toast.error(error.response?.data?.error || 'Failed to start work')
    } finally {
      setStatusLoading(false)
    }
  }

  const handleComplete = async () => {
    setStatusLoading(true)
    try {
      await api.post(`/maintenance/work-orders/${id}/complete/`, {
        resolution: resolutionDescription,
        actual_hours: actualHours || null,
        labor_notes: laborNotes,
      })
      toast.success('Work order completed successfully')
      setCompleteModalOpen(false)
      setResolutionDescription('')
      setActualHours('')
      setLaborNotes('')
      fetchWorkOrder()
    } catch (error) {
      console.error('Error completing work order:', error)
      toast.error(error.response?.data?.error || 'Failed to complete work order')
    } finally {
      setStatusLoading(false)
    }
  }

  const handleClose = async () => {
    setStatusLoading(true)
    try {
      await api.post(`/maintenance/work-orders/${id}/close/`)
      toast.success('Work order closed successfully')
      fetchWorkOrder()
    } catch (error) {
      console.error('Error closing work order:', error)
      toast.error(error.response?.data?.error || 'Failed to close work order')
    } finally {
      setStatusLoading(false)
    }
  }

  const handlePrint = () => {
    navigate(`/work-orders/${id}/print`)
  }

  const handleGenerateJobCard = async () => {
    setJobCardLoading(true)
    try {
      const response = await api.post('/maintenance/job-cards/generate_from_work_order/', {
        work_order_id: id,
      })
      toast.success('Job card generated successfully')
      // Navigate to the job card
      navigate(`/maintenance/job-cards/${response.data.id}/print`)
    } catch (error) {
      console.error('Error generating job card:', error)
      if (error.response?.data?.job_card_id) {
        // Job card already exists, offer to view it
        toast.info('A job card already exists for this work order')
        navigate(`/maintenance/job-cards/${error.response.data.job_card_id}/print`)
      } else {
        toast.error(error.response?.data?.error || 'Failed to generate job card')
      }
    } finally {
      setJobCardLoading(false)
    }
  }

  const handleEditSuccess = () => {
    fetchWorkOrder()
    setEditFormOpen(false)
    toast.success('Work order updated successfully')
  }

  const handleFileUpload = async (event) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('work_order', id)
        formData.append('file_name', file.name)
        formData.append('file_type', file.type.startsWith('image/') ? 'image' : 'document')
        formData.append('description', '')

        await api.post('/maintenance/work-order-attachments/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      }
      toast.success('File(s) uploaded successfully')
      fetchWorkOrder()
    } catch (error) {
      console.error('Error uploading file:', error)
      toast.error('Failed to upload file')
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDeleteAttachment = async () => {
    if (!deleteAttachmentId) return
    setDeleting(true)
    try {
      await api.delete(`/maintenance/work-order-attachments/${deleteAttachmentId}/`)
      toast.success('Attachment deleted')
      setAttachments(attachments.filter(a => a.id !== deleteAttachmentId))
      setDeleteAttachmentId(null)
    } catch (error) {
      console.error('Error deleting attachment:', error)
      toast.error('Failed to delete attachment')
    } finally {
      setDeleting(false)
    }
  }

  const getFileIcon = (fileType) => {
    if (fileType === 'image') return <Image color="primary" />
    if (fileType === 'document') return <Description color="info" />
    return <InsertDriveFile />
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (loading) {
    return <LoadingSpinner message="Loading work order details..." />
  }

  if (!workOrder) {
    return (
      <Box textAlign="center" py={4}>
        <Typography>Work order not found</Typography>
        <Button onClick={() => navigate('/work-orders')} sx={{ mt: 2 }}>
          Back to Work Orders
        </Button>
      </Box>
    )
  }

  const getStatusActions = () => {
    const canEdit = hasPermission('work_orders.edit')
    const noPermissionMsg = 'You do not have permission to perform this action. Contact your administrator.'

    const renderButton = (icon, label, color, onClick) => (
      <Tooltip title={!canEdit ? noPermissionMsg : ''} arrow>
        <span>
          <Button
            startIcon={icon}
            color={color}
            onClick={() => {
              if (!canEdit) {
                toast.error(noPermissionMsg)
                return
              }
              onClick()
            }}
            loading={statusLoading}
            disabled={!canEdit}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    )

    switch (workOrder.status) {
      case 'open':
      case 'pending':
      case 'assigned':
        return renderButton(<PlayArrow />, 'Start Work', 'info', handleStartWork)
      case 'in_progress':
        return renderButton(<Check />, 'Complete', 'success', () => setCompleteModalOpen(true))
      case 'completed':
        return renderButton(<Close />, 'Close', 'secondary', handleClose)
      default:
        return null
    }
  }

  return (
    <Box>
      <PageHeader
        title={workOrder.title}
        subtitle={`WO #${workOrder.work_order_number}`}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Work Orders', path: '/work-orders' },
          { label: workOrder.work_order_number },
        ]}
        actions={
          <Box display="flex" gap={1} flexWrap="wrap">
            {getStatusActions()}
            {(workOrder.status === 'completed' || workOrder.status === 'closed') && (
              <PermissionGate permission="job_cards.generate">
                <Button
                  variant="outlined"
                  startIcon={<Assignment />}
                  onClick={handleGenerateJobCard}
                  loading={jobCardLoading}
                  color="success"
                >
                  Generate Job Card
                </Button>
              </PermissionGate>
            )}
            <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>
              Print
            </Button>
            <Tooltip
              title={!hasPermission('work_orders.edit') ? 'You do not have permission to edit work orders. Contact your administrator.' : ''}
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => {
                    if (!hasPermission('work_orders.edit')) {
                      toast.error('You do not have permission to edit work orders. Contact your administrator.')
                      return
                    }
                    setEditFormOpen(true)
                  }}
                  disabled={!hasPermission('work_orders.edit')}
                >
                  Edit
                </Button>
              </span>
            </Tooltip>
          </Box>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card title="Work Order Details">
            <Grid container spacing={2}>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box mt={0.5}>
                  <StatusChip status={workOrder.status} />
                </Box>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Priority
                </Typography>
                <Box mt={0.5}>
                  <StatusChip status={workOrder.priority} />
                </Box>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Category
                </Typography>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                  {workOrder.category || workOrder.work_type || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Asset
                </Typography>
                <Typography variant="body2">
                  {workOrder.asset_name || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Assigned To
                </Typography>
                <Typography variant="body2">
                  {workOrder.assigned_to_name || 'Unassigned'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Section/Unit
                </Typography>
                <Typography variant="body2">
                  {workOrder.department_name || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Due Date
                </Typography>
                <Typography variant="body2">
                  {workOrder.due_date ? formatDate(workOrder.due_date) : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Requested By
                </Typography>
                <Typography variant="body2">
                  {workOrder.requested_by_name || workOrder.created_by_name || '-'}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" gutterBottom>
              Description
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {workOrder.description || 'No description provided'}
            </Typography>

            {workOrder.resolution_description && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Resolution
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {workOrder.resolution_description}
                </Typography>
              </>
            )}
          </Card>

          {/* Attachments Section */}
          <Card title="Attachments" sx={{ mt: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2" color="text.secondary">
                {attachments.length} file(s) attached
              </Typography>
              <PermissionGate permission="work_orders.edit">
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                />
                <Button
                  startIcon={<CloudUpload />}
                  onClick={() => fileInputRef.current?.click()}
                  loading={uploading}
                  size="small"
                >
                  Upload Files
                </Button>
              </PermissionGate>
            </Box>

            {attachments.length === 0 ? (
              <Box textAlign="center" py={3} sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
                <AttachFile sx={{ fontSize: 40, color: 'grey.400' }} />
                <Typography variant="body2" color="text.secondary" mt={1}>
                  No attachments yet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Upload images, documents, or reports
                </Typography>
              </Box>
            ) : (
              <List dense>
                {attachments.map((attachment) => (
                  <ListItem
                    key={attachment.id}
                    sx={{
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': { bgcolor: 'grey.100' }
                    }}
                  >
                    <ListItemIcon>
                      {getFileIcon(attachment.file_type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          component="a"
                          href={attachment.file_url || attachment.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {attachment.file_name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(attachment.file_size)} | Uploaded by {attachment.uploaded_by_name || 'Unknown'} on {formatDate(attachment.uploaded_at)}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <PermissionGate permission="work_orders.edit">
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          onClick={() => setDeleteAttachmentId(attachment.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </PermissionGate>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Card>

          {/* Parts Used Section */}
          {workOrder.parts_used && workOrder.parts_used.length > 0 && (
            <Card title="Parts Used" sx={{ mt: 3 }}>
              <List dense>
                {workOrder.parts_used.map((part) => (
                  <ListItem key={part.id}>
                    <ListItemText
                      primary={part.spare_part_name}
                      secondary={`Part #: ${part.part_number} | Qty: ${part.quantity_used}`}
                    />
                    <Typography variant="body2">
                      {part.unit_cost ? `UGX ${Number(part.unit_cost * part.quantity_used).toLocaleString()}` : '-'}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card title="Time & Cost">
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Estimated Hours
                </Typography>
                <Typography variant="body2">
                  {workOrder.estimated_hours || '-'} hrs
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Actual Hours
                </Typography>
                <Typography variant="body2">
                  {workOrder.actual_hours || '-'} hrs
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Downtime
                </Typography>
                <Typography variant="body2">
                  {workOrder.downtime_hours ? `${workOrder.downtime_hours} hrs` : '-'}
                </Typography>
              </Box>
            </Box>
          </Card>

          <Card title="Timeline" sx={{ mt: 3 }}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {formatDateTime(workOrder.created_at)}
                </Typography>
              </Box>
              {workOrder.start_time && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Started
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(workOrder.start_time)}
                  </Typography>
                </Box>
              )}
              {workOrder.completion_time && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Completed
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(workOrder.completion_time)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>

          {/* Job Card Acknowledgement */}
          {jobCard && (
            <Card title="Job Card" sx={{ mt: 3 }}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Job Card Number
                  </Typography>
                  <Typography variant="body2">
                    {jobCard.job_card_number}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    User Acknowledgement
                  </Typography>
                  <Box mt={0.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Chip
                      label={jobCard.user_acknowledged ? 'ACKNOWLEDGED' : 'PENDING'}
                      color={jobCard.user_acknowledged ? 'success' : 'warning'}
                      size="small"
                    />
                    {jobCard.user_acknowledged && (
                      <Typography variant="caption" color="text.secondary">
                        {jobCard.user_acknowledged_by_name}
                        {jobCard.user_acknowledged_at ? ` · ${formatDate(jobCard.user_acknowledged_at)}` : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Supervisor Acknowledgement
                  </Typography>
                  <Box mt={0.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Chip
                      label={jobCard.is_acknowledged ? 'ACKNOWLEDGED' : 'PENDING'}
                      color={jobCard.is_acknowledged ? 'success' : 'warning'}
                      size="small"
                    />
                    {jobCard.is_acknowledged && (
                      <Typography variant="caption" color="text.secondary">
                        {jobCard.acknowledged_by_name}
                        {jobCard.acknowledged_at ? ` · ${formatDate(jobCard.acknowledged_at)}` : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Assignment />}
                  onClick={() => navigate(`/maintenance/job-cards/${jobCard.id}/print`)}
                >
                  View Job Card
                </Button>
              </Box>
            </Card>
          )}

          {/* Labor Records */}
          {workOrder.labor_records && workOrder.labor_records.length > 0 && (
            <Card title="Labor Records" sx={{ mt: 3 }}>
              <List dense>
                {workOrder.labor_records.map((labor) => (
                  <ListItem key={labor.id}>
                    <ListItemText
                      primary={labor.technician_name}
                      secondary={`${labor.hours_worked} hrs on ${formatDate(labor.work_date)}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Edit Form Modal */}
      <WorkOrderForm
        open={editFormOpen}
        onClose={() => setEditFormOpen(false)}
        workOrder={workOrder}
        onSuccess={handleEditSuccess}
      />

      {/* Complete Work Order Modal */}
      <Modal
        open={completeModalOpen}
        onClose={() => setCompleteModalOpen(false)}
        title="Complete Work Order"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setCompleteModalOpen(false)} disabled={statusLoading}>
              Cancel
            </Button>
            <Button color="success" onClick={handleComplete} loading={statusLoading}>
              Complete
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            fullWidth
            label="Resolution Description"
            multiline
            rows={4}
            value={resolutionDescription}
            onChange={(e) => setResolutionDescription(e.target.value)}
            placeholder="Describe the work completed and resolution..."
          />
          <TextField
            fullWidth
            label="Actual Hours Worked"
            type="number"
            value={actualHours}
            onChange={(e) => setActualHours(e.target.value)}
            inputProps={{ min: 0, step: 0.5 }}
            helperText="This will be logged as a labor record"
          />
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={2}
            value={laborNotes}
            onChange={(e) => setLaborNotes(e.target.value)}
            placeholder="Additional notes about the work performed..."
          />
        </Box>
      </Modal>

      {/* Delete Attachment Confirmation Modal */}
      <Modal
        open={!!deleteAttachmentId}
        onClose={() => setDeleteAttachmentId(null)}
        title="Delete Attachment"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteAttachmentId(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDeleteAttachment} loading={deleting}>
              Delete
            </Button>
          </>
        }
      >
        Are you sure you want to delete this attachment?
      </Modal>
    </Box>
  )
}

export default WorkOrderDetail
